import { DropdownButton } from '@neo4j-ndl/react';
import type { InstanceSummary } from '@nx/state';
import { consoleApi, useConnection, useUnsafeAppContext } from '@nx/state';
import { skipToken } from '@reduxjs/toolkit/query';
import { useRef, useState } from 'react';

import { ConnectionStatusIndicator } from './connection-status-indicator';
import { InfoItem } from './info-item';
import { InstanceMenu } from './instance-menu';

type Connection = ReturnType<typeof useConnection>;

const getInstanceName = (instances: InstanceSummary[], connection: Connection) => {
  if (connection.status.isConnected) {
    // Instance is now always undefined in multi-DB mode until we fix the bolt url.
    // because the auraId is the default database id, not the instance id.
    const instance = instances.find(({ id }) => id === connection.metadata?.auraId);

    if (instance) {
      return instance.name;
    }

    return connection.metadata?.name ?? 'Unknown';
  }

  return '';
};

export function InstanceMenuButton() {
  const connection = useConnection();
  const { activeProjectId } = useUnsafeAppContext();
  const { data: instances = [] } = consoleApi.useListInstancesQuery(activeProjectId ?? skipToken);

  const buttonRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const showInstanceName = !connection.status.isDisconnected;

  return (
    <>
      <DropdownButton
        aria-label="Toggle instance menu"
        htmlAttributes={{ 'data-testid': 'instance-menu-button' }}
        size="small"
        ref={buttonRef}
        isOpen={isMenuOpen}
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        leftIcon={<ConnectionStatusIndicator />}
        className="border-none"
      >
        <InfoItem
          label={showInstanceName ? 'Instance:' : 'No instance connected'}
          value={getInstanceName(instances, connection)}
        />
      </DropdownButton>

      <InstanceMenu isMenuOpen={isMenuOpen} anchorEl={buttonRef} onClose={() => setIsMenuOpen(false)} />
    </>
  );
}
